import React, { useState } from 'react';
import { IPDetail } from '../../../types/interfaces';
import IPDetailInfo from './IPDetailInfo';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';

interface Domain {
  id: number;
  domain_name: string;
}

interface IPsProps {
  selectedDomain: string | null;
  ipsData: IPDetail[];
  domains: Domain[];
  onDomainChange: (domain: string) => void;
  onIPSelect?: (ipDetail: IPDetail) => void;
}

const IPs: React.FC<IPsProps> = ({
  selectedDomain,
  ipsData,
  domains,
  onDomainChange,
  onIPSelect,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const dataPerPage = 10;
  const [selectedIP, setSelectedIP] = useState<IPDetail | null>(null);

  const handleDomainSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onDomainChange(e.target.value);
    setCurrentPage(1);
    setSelectedIP(null);
  };

  const handleIPClick = (ipDetail: IPDetail) => {
    setSelectedIP(ipDetail);
    if (onIPSelect) onIPSelect(ipDetail);
  };

  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;
  const currentData = ipsData.slice(indexOfFirstData, indexOfLastData);
  const totalPages = Math.ceil(ipsData.length / dataPerPage);

  const goToNextPage = () => setCurrentPage(prev => (prev < totalPages ? prev + 1 : prev));
  const goToPreviousPage = () => setCurrentPage(prev => (prev > 1 ? prev - 1 : prev));

  return (
    <div className="flex flex-col md:flex-row space-x-6 p-6 mb-6">
      {/* Panel izquierdo: listado de IPs */}
      <div className="w-full md:w-1/2">
        <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
          <div className="flex items-center mb-6">
            <div className="w-3 h-10 bg-gray-800 mr-4"></div>
            <h3 className="text-4xl font-bold">{t('ips.title')}</h3>
          </div>

          {/* Selector de dominio */}
          <div className="mb-4">
            <label
              htmlFor="domain-select"
              className="block text-2xl font-medium text-gray-700 mb-4"
            >
              {t('ips.select_domain')}
            </label>
            <select
              id="domain-select"
              value={selectedDomain || ''}
              onChange={handleDomainSelect}
              className="block w-full max-w-md mx-auto p-2 border border-gray-800 bg-gray-800 text-white font-bold rounded-md"
            >
              {domains.map(domain => (
                <option
                  key={domain.id}
                  value={domain.domain_name}
                  className="text-gray-800"
                >
                  {domain.domain_name}
                </option>
              ))}
            </select>
          </div>

          {/* Tabla de IPs */}
          {currentData.length > 0 ? (
            <>
              <div style={{ minHeight: '550px' }}>
                <table className="min-w-full bg-white table-auto">
                  <thead>
                    <tr className="bg-gray-800 text-white">
                      <th className="py-3 px-6 text-center text-sm font-semibold tracking-wider">
                        {t('ips.table.ip')}
                      </th>
                      <th className="py-3 px-6 text-center text-sm font-semibold tracking-wider">
                        {t('ips.table.vulnerabilities')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((ipDetail, i) => (
                      <tr
                        key={i}
                        className="text-gray-700 bg-gray-50 even:bg-gray-100 hover:bg-gray-200 cursor-pointer"
                        onClick={() => handleIPClick(ipDetail)}
                      >
                        <td className="py-3 px-6 border text-center">
                          {ipDetail.ip}
                        </td>
                        <td className="py-3 px-6 border text-center">
                          {ipDetail.vulnerabilities?.length ? (
                            <FaExclamationCircle
                              className="text-red-500 mx-auto"
                              title={t('ips.has_vulns')}
                            />
                          ) : (
                            <FaCheckCircle
                              className="text-green-500 mx-auto"
                              title={t('ips.no_vulns')}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {ipsData.length > dataPerPage && (
                <div className="mt-6 flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {t('ips.pagination.info', {current: currentPage, total: totalPages})}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 text-sm font-medium rounded-lg ${
                        currentPage === 1 ? 'bg-gray-300 text-gray-500' : 'bg-gray-800 text-white'
                      }`}
                    >
                      {t('ips.pagination.previous')}
                    </button>
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 text-sm font-medium rounded-lg ${
                        currentPage === totalPages ? 'bg-gray-300 text-gray-500' : 'bg-gray-800 text-white'
                      }`}
                    >
                      {t('ips.pagination.next')}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center min-h-[200px]">
              <p className="text-center text-xl text-gray-600">
                {t('ips.no_ips')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Panel derecho: detalles de la IP seleccionada */}
      <div className="w-full md:w-1/2 bg-white p-6 rounded-lg shadow-lg">
        {selectedIP ? (
          <IPDetailInfo ipDetail={selectedIP} onClose={() => setSelectedIP(null)} />
        ) : (
          <>
            <div className="flex items-center mb-6">
              <div className="w-3 h-10 bg-gray-800 mr-4"></div>
              <h3 className="text-3xl font-bold">{t('ips.info_title')}</h3>
            </div>
            <p className="text-gray-700 mb-4 text-xl">
              {t('ips.info_paragraph')}
            </p>
            <strong className="block text-gray-700 mt-4 text-xl">
              {t('ips.info_instruction')}
            </strong>
          </>
        )}
      </div>
    </div>
  );
};

export default IPs;
